import React, {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  KeyboardEventHandler,
  useCallback,
} from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { useControlledInputHybrid, useForwardedRef } from '@hcs/hooks';

import { PII_MASK_CSS_CLASS } from '../../../../constants';
import { SearchIcon } from '../../../../svgs/icons';
import { CloseIcon } from '../../../../svgs/icons/navigation';
import { SearchInputStyle } from '../SearchInputStyle';

import styles from './SearchInput.module.css';

export interface SearchInputTheme {
  InputElement: string;
}
export interface SearchInputProps {
  className?: string;
  dataHcName: string;
  placeholder?: string;
  disabled?: boolean;
  theme?: SearchInputTheme;
  value: string;
  /** For when this is rendered in a MultiSearch component */
  inMultiSearch?: boolean;
  onChangeDebounce?: number;
  withClear?: boolean;
  onChange: (value: string) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  onClick?: VoidFunction;
  noCapture?: boolean;
}

export const SearchInput = forwardRef(
  (props: SearchInputProps, ref: React.Ref<HTMLInputElement>) => {
    const {
      className,
      dataHcName,
      placeholder,
      theme,
      value: valueProp,
      disabled,
      onChange,
      onKeyUp,
      noCapture = false,
      onBlur,
      inMultiSearch,
      withClear = false,
      onChangeDebounce,
      onClick,
    } = props;
    const onChangeCallback: SearchInputProps['onChange'] = useCallback(
      onChangeDebounce
        ? debounce(onChange, onChangeDebounce, { trailing: true })
        : onChange,
      [onChange, onChangeDebounce],
    );
    const [value, setValue] = useControlledInputHybrid(valueProp);
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (event) {
        const value = event.target.value || '';
        setValue(value);
        onChangeCallback(value);
      }
    };
    const handleClear = () => {
      const clearedValue = '';
      setValue(clearedValue);
      onChangeCallback(clearedValue);
    };
    const forwardedRef = useForwardedRef(ref);
    return (
      <SearchInputStyle
        className={classNames(
          {
            [styles.disabled]: disabled,
          },
          className,
        )}
        unstyled={inMultiSearch}
        icon={
          !inMultiSearch ? (
            <SearchIcon dataHcName={'search-icon'} size="sm" />
          ) : null
        }
      >
        <input
          ref={ref ? forwardedRef : undefined}
          className={classNames(styles.InputElement, theme?.InputElement, {
            [PII_MASK_CSS_CLASS]: noCapture,
          })}
          data-hc-name={dataHcName}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          onKeyUp={onKeyUp}
          onBlur={onBlur}
          onClick={onClick}
        />
        {withClear && value !== '' && (
          <CloseIcon className={styles.ClearIcon} onClick={handleClear} />
        )}
      </SearchInputStyle>
    );
  },
);
